import type { ProductMetric } from "@/features/products";
import { apiFetch } from "@/lib/jwt-auth-client";

/**
 * Recalculate all derived fields (GM1-3, verdict, etc.) from the base inputs.
 *
 * GM1 = RSP - COGS                          (product margin)
 * GM2 = GM1 - deliveryCost                  (landed margin)
 * GM3 = GM2 - processingFee - CAC           (net margin after selling)
 */
export function recalcDerivedMetrics(pm: ProductMetric): void {
  const rsp = pm.rsp;
  const cogs = pm.cogs;
  const deliveryCost = pm.deliveryCost ?? 0;
  const cac = pm.cac ?? 0;

  const gm1 = rsp - cogs;
  pm.gm1 = Math.round(gm1 * 100) / 100;
  pm.gm1Pct = rsp > 0 ? Math.round((gm1 / rsp) * 10000) / 100 : 0;
  pm.pcRatio = cogs > 0 ? Math.round((rsp / cogs) * 100) / 100 : 0;

  const gm2 = gm1 - deliveryCost;
  pm.gm2 = Math.round(gm2 * 100) / 100;

  const processingFee = rsp * 0.029 + 0.3;
  const gm3 = gm2 - processingFee - cac;
  pm.gm3 = Math.round(gm3 * 100) / 100;

  // Breakeven CPA = max CAC you can afford before GM3 goes negative
  const gm3AtZeroCac = gm2 - processingFee;
  pm.breakevenCpa = Math.round(Math.max(gm3AtZeroCac, 0) * 100) / 100;

  const avgOrderValue = rsp > 0 ? rsp : 1;
  const ltvMult = pm.ltv12m / avgOrderValue;
  pm.ltvBreakevenCpa = Math.round(gm3AtZeroCac * ltvMult * 100) / 100;

  pm.ltvCac = cac > 0 ? Math.round((pm.ltv12m / cac) * 100) / 100 : null;

  const ltvCac = pm.ltvCac;
  if (gm3AtZeroCac <= 0) pm.verdict = "not_viable";
  else if (cac === 0) pm.verdict = "needs_cac_data";
  else if (ltvCac !== null && ltvCac >= 3) pm.verdict = "strong";
  else if (ltvCac !== null && ltvCac >= 2) pm.verdict = "viable";
  else if (ltvCac !== null && ltvCac >= 1) pm.verdict = "marginal";
  else pm.verdict = "not_viable";

  if (pm.unitsSold > 0) {
    const dailySales = pm.unitsSold / 180;
    pm.daysOfStock = dailySales > 0 ? Math.round(pm.stockLevel / dailySales) : null;
  }
}

/**
 * Merge incoming pipeline product metrics with any manual user edits.
 * If a product was manually edited, the overridden fields are re-applied
 * on top of the new pipeline data and derived metrics are recalculated.
 */
export function mergeProductMetrics(
  incoming: ProductMetric[],
  existing: ProductMetric[] | undefined,
): ProductMetric[] {
  if (!existing) return incoming;
  const existingMap = new Map(existing.map((m) => [m.productId, m]));

  return incoming.map((newPm) => {
    const old = existingMap.get(newPm.productId);
    const overrides = old?._manualOverrides;
    if (!overrides || Object.keys(overrides).length === 0) return newPm;

    // Re-apply manual edits on top of fresh pipeline data
    const merged = { ...newPm, ...overrides, _manualOverrides: overrides };
    if (overrides.cogs !== undefined) merged.cogsSource = "manual";
    recalcDerivedMetrics(merged);
    return merged;
  });
}

/** Persist a single product metric update to the backend. */
export async function persistProductMetricUpdate(
  storeId: string,
  productId: string,
  field: string,
  value: number,
): Promise<void> {
  const ALLOWED_FIELDS = ["cogs", "deliveryCost", "cac", "ltv12m", "stockLevel", "leadTime"];
  if (!ALLOWED_FIELDS.includes(field)) return;

  try {
    await apiFetch("/core/coagent/product-metric-update", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ storeId, productId, field, value }),
    });
  } catch {
    // Best-effort persistence — local state is already updated
  }
}
